import { useSnackbarContext } from 'Context/SnackbarContext';
import { useMessageInput } from 'Components/ComponentLibrary/Components/Uncategorized/MessageInput';
import useLink from 'Hooks/useLink';
import fr from 'facet-rest';
import useDebounceWithCallback from 'Hooks/useDebounceWithCallback';
import Sentry from 'Integrations/Sentry';
import React, { useEffect } from 'react';
import { useRightColumnContext } from 'Context/RightColumnContext';
import { noop } from 'Helpers/PropTypes';

const useScenarioQuestionMessageContext = (
  scenarioQuestionResource,
) => {
  const {
    collection, setCollection, loading: isMessageLoading, loadingError: messageLoadingError,
  } = useLink(
    scenarioQuestionResource.relationship(fr.relationships.draftMessage.name).link('related'),
  );

  const { clearRightColumnCollection } = useRightColumnContext();

  const [isMessageSending, setMessageSending] = React.useState(false);
  const [draftMessageResource, setDraftMessageResource] = React.useState(collection?.resources().head());
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();

  const loadDraftMessage = async () => {
    try {
      let draftMessageCollection = collection;
      if (collection && !collection.resources().head()) {
        const [newDraftMessage] = collection.create({ addToCollection: true });
        newDraftMessage.attr('isDraft', 'true');
        draftMessageCollection = await newDraftMessage.submit();
        setCollection(draftMessageCollection);
      }
      setDraftMessageResource(draftMessageCollection?.resources().head());
    } catch (err) {
      if (err.errors) {
        displayErrorMessage(err, err.errors);
      } else {
        displayErrorMessage(err, 'Error: Failed to load message, please try again');
      }
    }
  };

  useEffect(() => {
    loadDraftMessage();
  }, [collection, draftMessageResource]);

  const autoSaveDraft = useDebounceWithCallback(async (newDraftMessageResource) => {
    setDraftMessageResource(newDraftMessageResource);

    // If this is not a draft, exit
    if (!newDraftMessageResource.isTruthy('isDraft')) {
      return;
    }
    try {
      await newDraftMessageResource.submit();
    } catch (e) {
      Sentry.createSentryErrorLog(e, e?.status);
    }
  }, 500, []);

  const sendMessage = React.useCallback(async (text) => {
    if (!draftMessageResource) {
      return null;
    }

    setMessageSending(true);
    try {
      draftMessageResource.attr('content', text);
      draftMessageResource.attr('isDraft', 'false');
      await draftMessageResource.submit();

      displaySuccess(scenarioQuestionResource.attr('messageSendSuccessText') ?? 'Your message was sent successfully');
      clearRightColumnCollection();
    } catch (err) {
      if (err.errors) {
        displayErrorMessage(err, err.errors);
      } else {
        displayErrorMessage(err, 'Error: Failed to send message, please try again');
      }
    } finally {
      setMessageSending(false);
    }

    return null;
  }, [draftMessageResource]);

  const {
    handleSubmit,
    handleContentChange,
  } = useMessageInput(
    collection?.resources().head(),
    sendMessage,
    noop,
    autoSaveDraft,
  );

  return {
    isMessageLoading,
    messageLoadingError,
    handleSendMessage: handleSubmit,
    isMessageSending,
    handleContentChange,
    draftMessageResource,
  };
};

export { useScenarioQuestionMessageContext };
